const SERVICE_FIELDS = new Set(['actionColumn', 'emptyColumn']);

export interface ITableSchemeColumn {
    header: string;
    field: string;
    dataType?: string;
    /** Остальные атрибуты колонки из схемы, без пустых. */
    attributes: ITableSchemeDetail[];
}

export interface ITableSchemeDetail {
    label: string;
    value: string;
    /** Короткая метка рядом с заголовком: тип значения, имя операции. */
    hint?: string;
    /** Вложенные элементы: changes, setValues, фильтры справочника и т.п. */
    children?: ITableSchemeDetail[];
}

/** Что родитель знает до загрузки грид-схемы: имя, ключ, формула видимости, адрес запроса. */
export interface ITableSchemeDebug {
    title: string;
    tableKey: string;
    visibilityRules?: string;
    sourcePath: string;
}

const COLUMN_ATTRIBUTES: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'fieldKey', label: 'fieldKey' },
    { key: 'docRequisite', label: 'docRequisite' },
    { key: 'index', label: 'index' },
    { key: 'alignment', label: 'выравнивание' },
    { key: 'width', label: 'ширина' },
    { key: 'minWidth', label: 'мин. ширина' },
    { key: 'allowFiltering', label: 'фильтр' },
    { key: 'allowSorting', label: 'сортировка' },
    { key: 'allowGrouping', label: 'группировка' },
    { key: 'allowLink', label: 'ссылка' },
    { key: 'allowEdit', label: 'редактирование' },
    { key: 'defaultHide', label: 'скрыта' },
    { key: 'showInColumnChooser', label: 'в выборе столбцов' },
    { key: 'sortField', label: 'sortField' },
    { key: 'sortIndex', label: 'sortIndex' },
    { key: 'sortOrder', label: 'sortOrder' },
    { key: 'dxGridCellTemplate', label: 'шаблон' },
    { key: 'userKeyField', label: 'userKeyField' },
    { key: 'isStatusColumn', label: 'колонка статуса' },
    { key: 'cssClasses', label: 'css' },
];

function primitiveText(value: unknown): string | undefined {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return undefined;
}

function formatAttribute(key: string, value: unknown): string | undefined {
    if (value == null || value === '') return undefined;
    if (Array.isArray(value)) {
        const items = value.flatMap((item) => {
            const text = primitiveText(item)?.trim();
            return text ? [text] : [];
        });
        return items.length === 0 ? undefined : items.join(', ');
    }
    if (typeof value === 'boolean') return value ? 'да' : 'нет';
    if (typeof value === 'number' && value === 0 && key === 'minWidth') return undefined;
    return primitiveText(value);
}

export function columnAttributes(column: object): ITableSchemeDetail[] {
    const source = column as Record<string, unknown>;
    return COLUMN_ATTRIBUTES.flatMap(({ key, label }) => {
        if (!(key in source)) return [];
        const value = formatAttribute(key, source[key]);
        return value === undefined ? [] : [{ label, value }];
    });
}

export function toSchemeDebugColumns<T extends { header?: string; field?: string; dataType?: string | null }>(
    columns: readonly T[],
): ITableSchemeColumn[] {
    return columns.flatMap((column) => {
        const field = column.field?.trim();
        const header = column.header?.trim();
        if (!field || !header || SERVICE_FIELDS.has(field)) return [];
        return [{
            header,
            field,
            dataType: column.dataType ? String(column.dataType) : undefined,
            attributes: columnAttributes(column),
        }];
    });
}
